// Simulated end-to-end encryption using Web Crypto API (AES-GCM)
// In a real production system, key derivation implies additional salting and potentially PBKDF2.

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;

// Derive a symmetric key from the user's UID for local encryption
const deriveKey = async (uid) => {
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(uid),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('devtracker-chatauth-salt'),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

// Encrypt a plaintext message
export const encryptMessage = async (uid, plainText) => {
  try {
    const key = await deriveKey(uid);
    const encoder = new TextEncoder();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedContent = await window.crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      key,
      encoder.encode(plainText)
    );

    // Combine IV and encrypted buffer into a base64 string for storage
    const encryptedArray = new Uint8Array(encryptedContent);
    const combined = new Uint8Array(iv.length + encryptedArray.length);
    combined.set(iv, 0);
    combined.set(encryptedArray, iv.length);

    return btoa(String.fromCharCode.apply(null, combined));
  } catch (err) {
    console.error("Encryption failed:", err);
    return null;
  }
};

// Decrypt a base64 encrypted payload
export const decryptMessage = async (uid, encryptedBase64) => {
  try {
    const key = await deriveKey(uid);
    const combinedStream = atob(encryptedBase64);
    const combined = new Uint8Array(combinedStream.length);
    for (let i = 0; i < combinedStream.length; i++) {
      combined[i] = combinedStream.charCodeAt(i);
    }

    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);

    const decryptedContent = await window.crypto.subtle.decrypt(
      { name: ALGORITHM, iv },
      key,
      encryptedData
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedContent);
  } catch (err) {
    console.error("Decryption failed:", err);
    return "[Encrypted/Unreadable]";
  }
};
